import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Megaphone } from 'lucide-react';
import { getActiveCampaigns } from '~/services/joc-club';
import type { CampaignItem } from '~/services/joc-club-admin';
import { getLocalized } from '~/utils/localized';

/**
 * Top campaign banner for the home page. Shows the most recent active campaign
 * as a full-width banner. Renders nothing when there is no active campaign
 * (the surrounding layout simply closes the gap) — decided over an empty box.
 */
interface CampaignBannerProps {
  /** Optional "details" action (e.g. open the Campaigns tab). */
  onDetail?: () => void;
}

export const CampaignBanner: React.FC<CampaignBannerProps> = ({ onDetail }) => {
  const { t, i18n } = useTranslation('common');
  const siteLocale = i18n.language;
  const [items, setItems] = useState<CampaignItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getActiveCampaigns()
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || items.length === 0) return null;

  const campaign = items[0];
  const title = getLocalized(campaign.title, siteLocale);
  const description = getLocalized(campaign.description, siteLocale);

  const Wrapper: React.ElementType = onDetail ? 'button' : 'div';

  return (
    <Wrapper
      data-testid="campaign-banner"
      onClick={onDetail}
      className={`relative block w-full overflow-hidden rounded-2xl bg-gradient-to-r from-neutral-900 to-red-950 p-5 text-left text-white ${
        onDetail ? 'transition-opacity hover:opacity-95' : ''
      }`}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-red-500/20 blur-2xl"
      />
      <div className="relative flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-red-300">
            <Megaphone className="h-3.5 w-3.5" />
            {t('joc_club.tab_campaigns')}
          </div>
          <p className="mt-1.5 text-lg font-bold leading-snug">{title || '—'}</p>
          {description && <p className="mt-1 line-clamp-2 text-sm text-white/70">{description}</p>}
        </div>
        {onDetail && (
          <span className="shrink-0 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium">
            {t('joc_club.view_details')}
          </span>
        )}
      </div>
    </Wrapper>
  );
};

export default CampaignBanner;
